import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Grid } from '@mui/material';
import { useMsal } from "@azure/msal-react";
import { getAuthenticatedAccount } from "../utils/accountUtils";
import HomeStats from './home/HomeStats';
import HomeRecent from './home/HomeRecent';
import strings from '../strings';

function Home() {
  const [stats, setStats] = useState({
    totalDocuments: null,
    totalRecords: null,
    recentDocuments: [],
    recentRecords: []
  });
  const [loading, setLoading] = useState(true);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const { instance, accounts } = useMsal();
  const tenantId = process.env.REACT_APP_AUTH_TENENT_ID;

  const fetchTotalDocuments = async () => {
    try {
      setLoadingDocuments(true);
      const account = await getAuthenticatedAccount(instance, accounts, tenantId);
      const response = await instance.acquireTokenSilent({
        scopes: [process.env.REACT_APP_AUTH_SP_SCOPE],
        account: account,
      });

      const graphApiUrl = process.env.REACT_APP_GRAPH_API_URL;
      const siteId = process.env.REACT_APP_SP_SITE_ID;
      const listUrl = process.env.REACT_APP_ENVIRONMENTAL_DOCUMENT_LIBRARY_LIST_URL;

      let totalDocuments = 0;
      let libraryNextLink = `${graphApiUrl}/sites/${siteId}/${listUrl}?$select=id&$top=100`;
      
      while (libraryNextLink) {
        const libraryCountResponse = await fetch(libraryNextLink, {
          headers: {
            Authorization: `Bearer ${response.accessToken}`,
            "Content-Type": "application/json",
          },
        });
        const libraryCountData = await libraryCountResponse.json();
        totalDocuments += libraryCountData.value.length;
        libraryNextLink = libraryCountData['@odata.nextLink'];
      }

      setStats(prev => ({ ...prev, totalDocuments }));
    } catch (error) {
      console.error("Error fetching total documents:", error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const fetchTotalRecords = async () => {
    try {
      setLoadingRecords(true);
      const account = await getAuthenticatedAccount(instance, accounts, tenantId);
      const response = await instance.acquireTokenSilent({
        scopes: [process.env.REACT_APP_AUTH_SP_SCOPE],
        account: account,
      });

      const graphApiUrl = process.env.REACT_APP_GRAPH_API_URL;
      const siteId = process.env.REACT_APP_SP_SITE_ID;
      const listId = process.env.REACT_APP_ENVIRONMENTAL_DOCUMENT_DATABASE_ID;

      let totalRecords = 0;
      let nextLink = `${graphApiUrl}/sites/${siteId}/lists/${listId}/items?$select=id&$top=100`;
      
      while (nextLink) {
        const databaseCountResponse = await fetch(nextLink, {
          headers: {
            Authorization: `Bearer ${response.accessToken}`,
            "Content-Type": "application/json",
            "Prefer": "outlook.timezone=\"UTC\""
          },
        });
        const databaseCountData = await databaseCountResponse.json();
        totalRecords += databaseCountData.value.length;
        nextLink = databaseCountData['@odata.nextLink'];
      }

      setStats(prev => ({ ...prev, totalRecords }));
    } catch (error) {
      console.error("Error fetching total records:", error);
    } finally {
      setLoadingRecords(false);
    }
  };

  const fetchRecentItems = useCallback(async () => {
    try {
      setLoading(true);
      const account = await getAuthenticatedAccount(instance, accounts, tenantId);
      const response = await instance.acquireTokenSilent({
        scopes: [process.env.REACT_APP_AUTH_SP_SCOPE],
        account: account,
      });

      const graphApiUrl = process.env.REACT_APP_GRAPH_API_URL;
      const siteId = process.env.REACT_APP_SP_SITE_ID;
      const listUrl = process.env.REACT_APP_ENVIRONMENTAL_DOCUMENT_LIBRARY_LIST_URL;
      const listId = process.env.REACT_APP_ENVIRONMENTAL_DOCUMENT_DATABASE_ID;

      const [libraryRecentResponse, databaseRecentResponse] = await Promise.all([
        fetch(`${graphApiUrl}/sites/${siteId}/${listUrl}?$expand=fields&$orderby=fields/Modified desc&$top=5`, {
          headers: {
            Authorization: `Bearer ${response.accessToken}`,
            "Content-Type": "application/json",
          },
        }),
        fetch(`${graphApiUrl}/sites/${siteId}/lists/${listId}/items?$expand=fields&$orderby=fields/field_3 desc&$top=5&$select=id,fields`, {
          headers: {
            Authorization: `Bearer ${response.accessToken}`,
            "Content-Type": "application/json",
            "Prefer": "outlook.timezone=\"UTC\""
          },
        })
      ]);

      const libraryRecentData = await libraryRecentResponse.json();
      const databaseRecentData = await databaseRecentResponse.json();

      setStats(prev => ({
        ...prev,
        recentDocuments: libraryRecentData.value || [],
        recentRecords: databaseRecentData.value || []
      }));
    } catch (error) {
      console.error("Error fetching recent items:", error);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, tenantId]);

  useEffect(() => {
    fetchRecentItems();
  }, [fetchRecentItems]);

  return (
    <Box sx={{ p: 3 }}>
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
        <Typography variant="h3" component="h1" gutterBottom>
          {strings.home.welcome}
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph>
          {strings.home.subtitle}
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <HomeStats
            stats={stats}
            loadingDocuments={loadingDocuments}
            loadingRecords={loadingRecords}
            onFetchTotalDocuments={fetchTotalDocuments}
            onFetchTotalRecords={fetchTotalRecords}
          />
          <HomeRecent
            stats={stats}
            loading={loading}
          />
        </Grid>
      </Paper>
    </Box>
  );
}

export default Home; 